/**
 * DigitalTwinModeStrategy
 * 
 * Strategy for Digital Twin mode.
 * Handles visualization of assets according to classes (hydrotwins, historical, forecast, calculated),
 * highlighting based on hover/lock, and hiding by class.
 */

import React from 'react';
import {
	MapModeStrategy,
	Infrastructure,
	PopupData,
	FetchParams,
	MapModeContext,
	DigitalTwinContext,
} from './MapModeStrategy';

// Lazy import of DigitalTwinAssetPopup component
const DigitalTwinAssetPopup = React.lazy(
	() => import('@/components/Dashboard/DigitalTwins/DigitalTwinAssetPopup')
);

// Digital Twin classes types
type DigitalTwinClass = 'hydrotwins' | 'historical' | 'forecast' | 'calculated';

class DigitalTwinModeStrategy implements MapModeStrategy {
	name = 'digitalTwin';
	displayName = 'Digital Twin Lens';
	dragRotate = false;
	touchRotate = false;

	/**
	 * In Digital Twin mode, filter by selected infrastructure types.
	 * Rain gauges are additionally filtered to only show those associated
	 * with the selected Digital Twin (via rainGauges in the resume data).
	 */
	filterInfrastructures(
		infras: Infrastructure[],
		context: MapModeContext,
		selectedTypes: string[]
	): Infrastructure[] {
		if (!infras) return [];

		const selectedDT = (context.selectedDigitalTwinsData as any[] | undefined)?.find(
			(dt: any) => dt.inpId?.toString() === context.selectedDigitalTwinId
		);

		if (!selectedDT) {
			return infras.filter((infra) => selectedTypes.includes(infra.infrastructureTypeName));
		}

		const rainGaugeAssetIds = new Set<number>(
			(selectedDT.raingauges ?? []).map((rg: any) => rg.assetId)
		);

		const dtAssetIds = new Set<number>([
			...(selectedDT.hydrotwins_detail ?? []).map((d: any) => d.assetId),
			...(selectedDT.historical_detail ?? []).map((d: any) => d.assetId),
			...(selectedDT.forecast_detail ?? []).map((d: any) => d.assetId),
			...(selectedDT.calculated_detail ?? []).map((d: any) => d.assetId),
		]);

		return infras.filter((infra) => {
			if (!selectedTypes.includes(infra.infrastructureTypeName)) return false;

			const isRainGauge =
				infra.infrastructureTypeName?.toUpperCase() === 'RAINGAGE' ||
				infra.infrastructureTypeName?.toUpperCase() === 'RAIN GAUGE';

			if (isRainGauge) {
				return rainGaugeAssetIds.has(infra.measurementeInfraestructureId);
			}

			return dtAssetIds.has(infra.measurementeInfraestructureId);
		});
	}

	/**
	 * Calculate opacities based on:
	 * 1. If the asset belongs to any class of the selected Digital Twin
	 * 2. If the class is hidden (hiddenParameterClasses)
	 * 3. If there is an active class (hover/lock) for highlighting
	 */
	calculateOpacities(
		infras: Infrastructure[],
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Record<number, number> {
		const opacities: Record<number, number> = {};
		
		if (!infras || infras.length === 0) return opacities;

		const {
			selectedDigitalTwinId,
			selectedDigitalTwinsData = [],
			hoveredParameterClass,
			lockedParameterClass,
			hiddenParameterClasses = new Set(),
		} = digitalTwinContext || {};

		// Find the selected Digital Twin
		const selectedDT = selectedDigitalTwinsData?.find(
			(dt: any) => dt.inpId?.toString() === selectedDigitalTwinId
		);

		// Determine the active class (priority: locked > hovered)
		const activeParameterClass = lockedParameterClass || hoveredParameterClass;

		// Calculate highlighted assets if there is an active class
		const highlightedAssetIds = this.getHighlightedAssets(context, digitalTwinContext);

		infras.forEach((infra) => {
			const infraId = infra.measurementeInfraestructureId;

			// Determine if the asset should be hidden
			let shouldBeHidden = false;

			if (selectedDT) {
				const isRainGauge =
					infra.infrastructureTypeName?.toUpperCase() === 'RAINGAGE' ||
					infra.infrastructureTypeName?.toUpperCase() === 'RAIN GAUGE';

				if (isRainGauge) {
					// Rain gauges are filtered separately via raingauges array — always show at full opacity
					opacities[infraId] = 1.0;
					return;
				}

				// Get all classes to which this asset belongs
				const assetClasses = this.getAssetClasses(infraId, selectedDT);

				// RULE: Asset hidden ONLY if it has classes AND all of them are in hiddenParameterClasses
				if (assetClasses.length > 0 && hiddenParameterClasses && hiddenParameterClasses.size > 0) {
					shouldBeHidden = assetClasses.every((cls) => hiddenParameterClasses.has(cls));
				}
			}

			// Determine opacity based on hiding state and highlighting
			if (shouldBeHidden) {
				opacities[infraId] = 0; // Completely hidden
			} else {
				// If there is active highlighting, check if this asset should be highlighted
				const shouldBeHighlighted = !highlightedAssetIds || highlightedAssetIds.has(infraId);
				opacities[infraId] = shouldBeHighlighted ? 1.0 : 0; // Visible or completely hidden
			}
		});

		return opacities;
	}

	/**
	 * In Digital Twin mode, return basic infrastructure data
	 * Digital Twin popup does not need additional fetch
	 */
	async handleAssetClick(
		infra: Infrastructure,
		context: MapModeContext
	): Promise<PopupData> {
		return {
			measurementeInfraestructureId: infra.measurementeInfraestructureId,
			name: infra.measurementInfraestructureName,
			measurementInfraestructureName: infra.measurementInfraestructureName,
			infrastructureTypeName: infra.infrastructureTypeName,
			expired: infra.mainMeasurementTarget?.expired,
		};
	}

	/**
	 * Popup component for Digital Twin mode
	 */
	PopupComponent = DigitalTwinAssetPopup;

	/**
	 * In Digital Twin mode, no additional parameters are sent to the fetch
	 */
	getFetchParams(context: MapModeContext): FetchParams {
		return {
			mode: '',
		};
	}

	/**
	 * Get the IDs of assets that should be highlighted based on the active class
	 */
	getHighlightedAssets(
		context: MapModeContext,
		digitalTwinContext?: DigitalTwinContext
	): Set<number> | null {
		if (!digitalTwinContext) return null;

		const {
			selectedDigitalTwinId,
			selectedDigitalTwinsData = [],
			hoveredParameterClass,
			lockedParameterClass,
		} = digitalTwinContext;

		// Prioritize locked over hovered
		const activeParameterClass = lockedParameterClass || hoveredParameterClass;

		if (!activeParameterClass || !selectedDigitalTwinId || !selectedDigitalTwinsData?.length) {
			return null; // No filtering when no active class
		}

		const selectedDT = selectedDigitalTwinsData.find(
			(dt: any) => dt.inpId?.toString() === selectedDigitalTwinId
		);

		if (!selectedDT) {
			return null;
		}

		let assetIds: number[] = [];

		switch (activeParameterClass) {
			case 'hydrotwins':
				assetIds = selectedDT.hydrotwins_detail?.map((detail: any) => detail.assetId) || [];
				break;
			case 'historical':
				assetIds = selectedDT.historical_detail?.map((detail: any) => detail.assetId) || [];
				break;
			case 'forecast':
				assetIds = selectedDT.forecast_detail?.map((detail: any) => detail.assetId) || [];
				break;
			case 'calculated':
				assetIds = selectedDT.calculated_detail?.map((detail: any) => detail.assetId) || [];
				break;
			default:
				return null;
		}

		return new Set(assetIds);
	}

	/**
	 * Props for the popup in Digital Twin mode
	 */
	getPopupProps(
		infraInfo: PopupData,
		context: MapModeContext,
		additionalData?: {
			selectedDigitalTwin?: any;
			isAdmin?: boolean;
		}
	): Record<string, any> {
		return {
			assetId: infraInfo.measurementeInfraestructureId,
			assetName: infraInfo.name || infraInfo.measurementInfraestructureName,
			assetType: infraInfo.infrastructureTypeName || 'Unknown',
			selectedDigitalTwin: additionalData?.selectedDigitalTwin || null,
			isAdmin: additionalData?.isAdmin || false,
		};
	}

	/**
	 * In Digital Twin mode, shift the GIS layer left so it runs parallel to the DT network
	 */
	getLinksLayerOffset(): number {
		return 0;
	}

	/**
	 * In Digital Twin mode, increase GIS link widths by 2px for better visibility
	 */
	getLinksWidthBonus(): number {
		return 1;
	}

	/**
	 * Helper: Get the classes to which an asset belongs
	 */
	private getAssetClasses(assetId: number, selectedDT: any): DigitalTwinClass[] {
		const classes: DigitalTwinClass[] = [];

		if (selectedDT.hydrotwins_detail?.some((d: any) => d.assetId === assetId)) {
			classes.push('hydrotwins');
		}
		if (selectedDT.historical_detail?.some((d: any) => d.assetId === assetId)) {
			classes.push('historical');
		}
		if (selectedDT.forecast_detail?.some((d: any) => d.assetId === assetId)) {
			classes.push('forecast');
		}
		if (selectedDT.calculated_detail?.some((d: any) => d.assetId === assetId)) {
			classes.push('calculated');
		}

		return classes;
	}
}

// Export singleton instance
export const digitalTwinModeStrategy = new DigitalTwinModeStrategy();
export default DigitalTwinModeStrategy;
